import type {
  DeliverableTypeOption,
  DeliverableView,
  ReferenceOption,
  TaskView,
  DeliverableEditability,
  DateChangeEntry,
  DateChangeReason,
} from "@fieldpath/shared";
import { DATE_CHANGE_REASONS, holdsContainerOpen, isPendingRequest, mdy } from "@fieldpath/shared";
import { useCallback, useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { rowOverdue } from "../overdue";
import { navigate } from "../router";
import { HelpButton } from "./HelpButton";
import { DateHint, PAST_DATE_BOX, todayLocal } from "./DateHint";
import { CancelToggle, CompleteToggle } from "./CancelToggle";
import { RequestPanel, RequestedChip } from "./RequestPanel";
import { CancelledTasks } from "./CancelledTasks";
import { SubjectFields } from "./FieldsPanel";
import { SearchSelect } from "./SearchSelect";
import { Crumbs } from "./Crumbs";
import { NewTask } from "./NewTask";
import { KindTag } from "./KindTag";
import { EditChip } from "./EditChip";
import { useTaskHover } from "./TaskHoverCard";

interface Props {
  fetcher: Fetcher;
  deliverable: DeliverableView;
  /** For the trail. Absent only while the parent is still loading it. */
  projectName?: string | null;
  onChanged: () => void;
}

/**
 * One deliverable: what it is called, when it is owed, and what its status
 * follows.
 *
 * These settings live here rather than on the project screen because they are
 * decisions about the deliverable itself, made once. Scattering them across a
 * list of rows made the project view a control panel when its job is to show
 * you where the work stands.
 */
export function DeliverableDetail({
  fetcher,
  deliverable,
  projectName,
  onChanged,
}: Props) {
  const [tasks, setTasks] = useState<TaskView[] | null>(null);
  const [types, setTypes] = useState<DeliverableTypeOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  /*
   * The Details form used to be open permanently — six inputs between the
   * heading and the work. **Most visits to this page are to read it**, not to
   * rename the deliverable, and a form sitting open says the opposite.
   *
   * Shut by default and NOT remembered: a form left open from last week would
   * invite an edit nobody came here to make.
   */
  const [editingDetails, setEditingDetails] = useState(false);
  // Closed either way: the only action left is undoing it.
  const dClosed =
    deliverable.cancelledAt !== null || deliverable.completedAt !== null;
  const [isPm, setIsPm] = useState<boolean | null>(null);
  // An admin may close a deliverable too (13 Oct 2026); the server decides.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    void api.amIAdmin(fetcher).then(setIsAdmin).catch(() => setIsAdmin(null));
  }, [fetcher]);
  // Taken from the `api.me` call this screen already makes, rather than a
  // second request for a fact the first one carries.
  const [meKey, setMeKey] = useState<string | null>(null);
  // One hook per screen: `bind` goes on each task row, `card` renders the
  // portal. **This was the last task list in the app without one** -- the
  // Phase 4 note claimed this page had it and it never did.
  const { bind, card } = useTaskHover(fetcher);

  const [name, setName] = useState(deliverable.name);
  const [typeId, setTypeId] = useState(deliverable.typeId ?? "");
  const [clientDueDate, setClientDueDate] = useState(deliverable.clientDueDate ?? "");
  const [ownerKey, setOwnerKey] = useState(deliverable.ownerKey ?? "");
  const [managers, setManagers] = useState<ReferenceOption[]>([]);
  // Who may change the owner and the client date (E5.4) -- the server's own
  // answer, read once per deliverable. Null while unknown: the fields show as
  // read-only until it arrives rather than offering what may be refused.
  const [may, setMay] = useState<DeliverableEditability | null>(null);
  const [dateHistory, setDateHistory] = useState<DateChangeEntry[]>([]);
  const [reasonCategory, setReasonCategory] = useState<DateChangeReason | "">("");
  const [reasonNote, setReasonNote] = useState("");

  const reload = useCallback(() => {
    void api
      .listProjectWork(fetcher, deliverable.projectId)
      .then((r) => setTasks(r.tasks.filter((t) => t.deliverableId === deliverable.id)))
      .catch((e: Error) => setError(e.message));
  }, [fetcher, deliverable.projectId, deliverable.id]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(reload, [reload, back]);

  // Same eligibility as the server: cancel needs it empty, complete needs
  // every task Done and a PM.
  // Cancelled tasks are listed apart (punch list #28). Cancelled and
  // hibernated work does not hold the deliverable open, as on the server:
  // `holdsContainerOpen` is its rule (hibernated since 23 Oct 2026).
  const live = tasks === null ? null : tasks.filter((t) => !t.cancelledAt);
  const cancelled = (tasks ?? []).filter((t) => t.cancelledAt);
  const today = todayLocal();
  const openD = (live ?? []).filter(holdsContainerOpen).length;
  // And the server's own answer on WHO (owner or admin, 12 Oct 2026).
  // LIVE tasks only, as the server counts them: a deliverable whose tasks were
  // all cancelled may be cancelled too. Until 21 Oct 2026 this counted the
  // cancelled ones, so the button never appeared for exactly that deliverable
  // (Jeremy, the "Delete" deliverable in Templates).
  const canCancelD = !dClosed && live !== null && live.length === 0 && may?.cancel === true;
  const canCompleteD =
    !dClosed && tasks !== null && (isPm === true || isAdmin === true) && openD === 0;

  useEffect(() => {
    void Promise.all([api.me(fetcher), api.projectManagers(fetcher)])
      .then(([me, pms]) => {
        setMeKey(me.employee?.ajeraKey ?? null);
        setIsPm(
          Boolean(me.employee && pms.some((p) => p.key === me.employee!.ajeraKey)),
        );
      })
      .catch(() => {
        // Unknown, not "no": the check failing is not evidence about anyone,
        // and not knowing who you are hides a highlight rather than a screen.
        setIsPm(null);
        setMeKey(null);
      });
  }, [fetcher]);

  useEffect(() => {
    void api.deliverableEditability(fetcher, deliverable.id).then(setMay).catch(() => setMay(null));
    void api
      .deliverableDateChanges(fetcher, deliverable.id)
      .then(setDateHistory)
      .catch(() => setDateHistory([]));
  }, [fetcher, deliverable]);

  useEffect(() => {
    void api.deliverableTypes(fetcher).then(setTypes).catch(() => undefined);
    void api.projectManagers(fetcher).then(setManagers).catch(() => undefined);
  }, [fetcher]);

  // Re-sync when the parent reloads after a save, so the fields show what was
  // actually stored rather than what was typed.
  useEffect(() => {
    setName(deliverable.name);
    setTypeId(deliverable.typeId ?? "");
    setClientDueDate(deliverable.clientDueDate ?? "");
    setOwnerKey(deliverable.ownerKey ?? "");
  }, [deliverable]);

  const save = async (patch: Parameters<typeof api.updateDeliverable>[2]): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await api.updateDeliverable(fetcher, deliverable.id, patch);
      onChanged();
      reload();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  // "Saved", shown for a few seconds after the details are saved and the
  // editor closes (punch list #43, Michelle, 19 Oct 2026). Saving left the
  // editor open with no sign it had worked.
  const [justSaved, setJustSaved] = useState(false);
  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(false), 4000);
    return () => clearTimeout(t);
  }, [justSaved]);

  // What its status follows is its PM's or an admin's (22 Oct 2026); the
  // pills still show which rule is in force, to everybody.
  const mayFollow = may?.details === true;

  // The client date moving LATER, which asks for a reason (E5.4).
  const pushingOut =
    deliverable.clientDueDate !== null && clientDueDate !== "" &&
    clientDueDate > deliverable.clientDueDate;

  const dirty =
    name !== deliverable.name ||
    typeId !== (deliverable.typeId ?? "") ||
    clientDueDate !== (deliverable.clientDueDate ?? "") ||
    ownerKey !== (deliverable.ownerKey ?? "");

  return (
    <>
      {card}
      <button
        className="linkish"
        onClick={() => navigate({ kind: "project", id: deliverable.projectId })}
        style={{ marginBottom: 12 }}
      >
        ← Back to project
      </button>

      <Crumbs projectId={deliverable.projectId} projectName={projectName} />

      <div className="row" style={{ marginBottom: 10 }}>
        {dClosed || canCompleteD ? (
          <CompleteToggle
            what="deliverable"
            completedAt={deliverable.completedAt}
            onToggle={async (next) => {
              await api.setDeliverableCompleted(fetcher, deliverable.id, next);
              onChanged();
              reload();
            }}
          />
        ) : null}
        {(dClosed && deliverable.cancelledAt && may?.cancel === true) || canCancelD ? (
          <CancelToggle
            what="deliverable"
            cancelledAt={deliverable.cancelledAt}
            onToggle={async (next) => {
              await api.setDeliverableCancelled(fetcher, deliverable.id, next);
              onChanged();
              reload();
            }}
          />
        ) : null}
      </div>

      {!dClosed && tasks !== null && !canCompleteD && !canCancelD ? (
        <p className="muted" style={{ marginTop: -4, marginBottom: 10 }}>
          {openD > 0
            ? `${openD} task${openD === 1 ? "" : "s"} still open — this deliverable can be marked complete once they are all done.`
            : isPm === false && isAdmin === false
              ? "Only a project manager or an admin can close a deliverable."
              : "Checking whether you can close this…"}
        </p>
      ) : null}

      {dClosed ? (
        <div
          className={deliverable.completedAt ? "closed-banner done" : "closed-banner"}
        >
          <strong>
            This deliverable is {deliverable.completedAt ? "completed" : "cancelled"}.
          </strong>{" "}
          No new tasks can be added to it. Reopen it to make changes.
        </div>
      ) : null}

      <div className="deliv-block">
        <h1 className="deliverable-h" style={{ marginTop: 0 }}>
          <KindTag kind="deliverable" size="head" />
          <span className="deliv-name">{deliverable.name}</span>
          {deliverable.completedAt ? (
            <span className="chip completed" style={{ marginLeft: 10 }}>
              Completed
            </span>
          ) : null}
          {deliverable.cancelledAt ? (
            <span className="chip cancelled" style={{ marginLeft: 10 }}>
              Cancelled
            </span>
          ) : null}
          {/* A request still waiting for a PM (punch list F140). */}
          {isPendingRequest(deliverable) ? <RequestedChip /> : null}
          {/* A closed deliverable cannot be edited, so it is not offered --
              nor, since 22 Oct 2026, to anybody but its PM or an admin, whose
              every field it is (`editability.details`). */}
          {dClosed || may?.details !== true ? null : (
            <EditChip
              title={
                editingDetails
                  ? "Stop editing this deliverable"
                  : "Edit this deliverable"
              }
              pressed={editingDetails}
              onClick={() => setEditingDetails((e) => !e)}
            />
          )}
          <HelpButton screens="deliverable" />
        </h1>
        <p className="muted" style={{ marginTop: 4 }}>
          <span
            className={`state ${deliverable.status.toLowerCase().replace(" ", "_")}`}
          >
            {deliverable.status}
          </span>{" "}
          {deliverable.clientDueDate
            ? `due to the client ${mdy(deliverable.clientDueDate)} · `
            : ""}
          {deliverable.gatingTaskTitle
            ? `follows “${deliverable.gatingTaskTitle}”`
            : "rolled up from every task"}
        </p>
        {!dClosed && may?.details === false ? (
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
            Only this deliverable's PM or an admin can edit it.
          </p>
        ) : null}
      </div>

      {/* Requested work (punch list F140): approving it approves the
          requested tasks inside it. */}
      {isPendingRequest(deliverable) ? (
        <RequestPanel
          fetcher={fetcher}
          kind="deliverables"
          id={deliverable.id}
          projectId={deliverable.projectId}
          mayDecide={may?.decideRequest === true}
          onDecided={() => {
            reload();
            onChanged();
          }}
        />
      ) : null}

      {error ? <div className="error">{error}</div> : null}
      {justSaved ? (
        <p className="saved-note" role="status">
          Saved.
        </p>
      ) : null}

      {/* Facts by default; the form behind the chip. Every field, every bit
          of help text and the Save/Discard pair are unchanged — only when
          they are on screen has changed. */}
      {!editingDetails ? (
        <div className="fact-grid">
          <Fact label="TYPE" value={deliverable.typeName ?? "not set"} />
          <Fact
            label="PM / TASK MANAGER"
            value={deliverable.ownerName ?? "nobody"}
          />
          <Fact
            label="DUE TO CLIENT"
            value={deliverable.clientDueDate ?? "not set"}
            mono
          />
          <Fact
            label="TASKS"
            value={`${tasks?.length ?? 0} · ${
              (tasks ?? []).filter((t) => t.status === "Done").length
            } done`}
            mono
          />
        </div>
      ) : (
      <div className="card">
        <h2>Details</h2>

        <div className="field">
          <label htmlFor="dd-name">Title</label>
          <input id="dd-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="dd-type">Type — optional, used for reporting</label>
          <select id="dd-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">Not categorised</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="dd-owner">PM / Task Manager</label>
          {may?.owner ? (
            <SearchSelect
              id="dd-owner"
              options={managers}
              value={ownerKey}
              onChange={setOwnerKey}
              emptyLabel="Nobody — start typing a name"
            />
          ) : (
            <p style={{ margin: 0, fontSize: 14 }}>
              {deliverable.ownerName ?? "Nobody"}{" "}
              <span className="muted">— only its PM or an admin can hand it on.</span>
            </p>
          )}
          <p className="muted" style={{ marginTop: 4 }}>
            Becomes the default PM / Task Manager for tasks raised here. Each
            task keeps its own, so changing this never rewrites who was
            accountable for work already done.
          </p>
        </div>

        <div className="field">
          <label htmlFor="dd-due">Due to client</label>
          {may?.clientDate ? (
            <>
              <input
                id="dd-due"
                type="date"
                {...PAST_DATE_BOX}
                value={clientDueDate}
                onChange={(e) => setClientDueDate(e.target.value)}
              />
              <DateHint />
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 14 }}>
              {deliverable.clientDueDate ? mdy(deliverable.clientDueDate) : "Not set"}{" "}
              <span className="muted">
                — only this deliverable's PM or an admin can change the client date.
              </span>
            </p>
          )}
          <p className="muted" style={{ marginTop: 4 }}>
            The commitment. Dates on the tasks below are internal working dates.
          </p>
        </div>

        {/* Later needs a reason (E5.4), as a task's date does: the categories
            exist to answer why work slipped. */}
        {pushingOut ? (
          <>
            <div className="field">
              <label htmlFor="dd-why">
                The client date moves from {deliverable.clientDueDate && mdy(deliverable.clientDueDate)} to {mdy(clientDueDate)}. Why?
              </label>
              <select
                id="dd-why"
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value as DateChangeReason)}
              >
                <option value="">Choose a reason…</option>
                {DATE_CHANGE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="dd-why-note">Detail (optional)</label>
              <input
                id="dd-why-note"
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                placeholder="Anything worth knowing later"
              />
            </div>
          </>
        ) : null}

        <div className="row">
          <button
            className="primary"
            disabled={busy || !dirty || name.trim() === "" || (pushingOut && !reasonCategory)}
            onClick={() =>
              void save({
                name: name.trim(),
                typeId: typeId || null,
                // Only what this person may change travels with the save.
                ...(may?.clientDate ? { clientDueDate: clientDueDate || null } : {}),
                ...(may?.owner ? { ownerKey: ownerKey || null } : {}),
                ...(pushingOut && reasonCategory
                  ? { reasonCategory, reasonNote: reasonNote.trim() || null }
                  : {}),
              }).then((ok) => {
                if (ok) {
                  setEditingDetails(false);
                  setJustSaved(true);
                }
              })
            }
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
          {dirty ? (
            <button
              disabled={busy}
              onClick={() => {
                setName(deliverable.name);
                setTypeId(deliverable.typeId ?? "");
                setClientDueDate(deliverable.clientDueDate ?? "");
                setOwnerKey(deliverable.ownerKey ?? "");
              }}
            >
              Discard
            </button>
          ) : null}
        </div>
      </div>
      )}

      {/* The deliverable's custom fields (E3/4). Nothing when it has none. */}
      <SubjectFields
        fetcher={fetcher}
        subject={{ level: "deliverable", id: deliverable.id }}
        title="Deliverable details"
        locked
      />

      {/* Every move of the date ERP owes the client (E5.4): who, when, from
          what to what, and why when it slipped. Until 12 Oct nothing recorded
          it at all. Shown only once there is something to show. */}
      {dateHistory.length > 0 ? (
        <section className="follows-block">
          <div className="section-label">
            <span>Client date history</span>
          </div>
          <ul className="history" style={{ marginTop: 10 }}>
            {dateHistory.map((d, i) => (
              <li key={i}>
                {d.previousDate ? mdy(d.previousDate) : "not set"} → {d.newDate ? mdy(d.newDate) : "cleared"}{" "}
                {d.reasonCategory ? <span className="chip">{d.reasonCategory}</span> : null}
                {d.note ? <span className="muted"> {d.note}</span> : null}
                {d.changedBy ? <span className="muted"> · {d.changedBy}</span> : null}
                <span className="muted"> · {mdy(d.changedAt.slice(0, 10))}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="follows-block">
        <div className="section-label">
          <span>Status follows</span>
        </div>
        <p className="muted" style={{ marginTop: 10 }}>
          By default a deliverable rolls up every task beneath it. Name one task
          instead when that task is what really decides — usually the report,
          since a Phase I is delivered when the report goes out, not when the
          field work finishes.
        </p>

        {/* Pills rather than a dropdown. The choices ARE the list of tasks,
            and a closed select hides which one is in force behind a click —
            on the one setting whose whole purpose is making that visible.
            Same values, same `save({ gatingTaskId })` call. */}
        <div className="follows-pills">
          <button
            className={`follow-pill${deliverable.gatingTaskId ? "" : " on"}`}
            disabled={busy || !mayFollow}
            onClick={() => void save({ gatingTaskId: null })}
          >
            All tasks
          </button>
          {(tasks ?? []).map((t) => (
            <button
              key={t.id}
              className={`follow-pill${
                deliverable.gatingTaskId === t.id ? " on" : ""
              }`}
              disabled={busy || !mayFollow}
              onClick={() => void save({ gatingTaskId: t.id })}
            >
              <KindTag kind="task" size="crumb" />
              {t.title}
            </button>
          ))}
        </div>

        {(tasks?.length ?? 0) === 0 ? (
          <p className="muted">Add a task to this deliverable first.</p>
        ) : may?.details === false ? (
          <p className="muted">Only this deliverable's PM or an admin can change which it follows.</p>
        ) : null}
      </section>

      {adding ? (
        <NewTask
          fetcher={fetcher}
          projectId={deliverable.projectId}
          deliverables={[deliverable]}
          // Already decided: the task is being raised FROM this deliverable,
          // so it belongs to it and is not asked about.
          fixedDeliverableId={deliverable.id}
          onCreated={() => {
            setAdding(false);
            reload();
            onChanged();
          }}
        />
      ) : null}

      <section className="dd-tasks">
        <div className="section-label">
          <span>Tasks in this deliverable</span>
          <div className="row">
            <span className="count">{live?.length ?? 0}</span>
            {dClosed ? null : (
              <button className="primary" onClick={() => setAdding((a) => !a)}>
                {adding ? (
                  "Cancel"
                ) : (
                  <>
                    <KindTag kind="task" size="row" />
                    Add task
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {deliverable.tasksPastClientDate > 0 ? (
          <div className="warn">
            {deliverable.tasksPastClientDate === 1
              ? "1 task is scheduled to finish after the client due date."
              : `${deliverable.tasksPastClientDate} tasks are scheduled to finish after the client due date.`}
          </div>
        ) : null}

        {live === null ? (
          <p className="muted">Loading…</p>
        ) : live.length === 0 ? (
          <p className="muted">
            {cancelled.length > 0
              ? "No live tasks — only cancelled ones, below."
              : "Nothing here yet. Open a task and set “Part of” to add it."}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Task</th>
                {/* WHO HAS IT -- the people on the task's CURRENT STAGE, the
                    same fact the project screen and the Overview show, so the
                    three cannot disagree about one task. */}
                <th>With</th>
                <th>Status</th>
                <th>Internal due</th>
              </tr>
            </thead>
            <tbody>
              {live.map((t) => {
                // Passed, and planned after the client's date: two facts,
                // never drawn alike (punch list F150).
                const overdue = rowOverdue(t, today);
                const afterClient =
                  deliverable.clientDueDate && t.dueDate
                    ? t.dueDate > deliverable.clientDueDate
                    : false;
                return (
                  <tr key={t.id} {...bind(t.id, t.title)}>
                    <td>
                      <KindTag kind="task" size="row" />
                      <button
                        className="linkish task-name"
                        onClick={() => navigate({ kind: "task", id: t.id })}
                      >
                        {t.title}
                      </button>
                      {t.id === deliverable.gatingTaskId ? (
                        <span className="chip" style={{ marginLeft: 8 }}>
                          decides status
                        </span>
                      ) : null}
                    </td>
                    <td className="twho">
                      {t.holders.length === 0
                        ? "nobody"
                        : /* Each name its own span, so yours can be marked
                             without marking the people beside it. */
                          t.holders.map((h, i) => (
                            <span key={h.key}>
                              {i > 0 ? ", " : ""}
                              <span
                                className={h.key === meKey ? "mine-name" : undefined}
                              >
                                {h.name}
                              </span>
                            </span>
                          ))}
                    </td>
                    <td>
                      <span
                        className={`state ${t.status.toLowerCase().replace(" ", "_")}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className={overdue ? "is-overdue" : "muted"}>
                      {t.dueDate ? mdy(t.dueDate) : "—"}
                      {overdue ? <span className="overdue-tag">Overdue</span> : null}
                      {afterClient ? (
                        <span className="after-client" title="Due after the deliverable's client date">
                          ⚠ after client date
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <CancelledTasks tasks={cancelled} />
      </section>
    </>
  );
}


/**
 * One labelled fact. Mono for anything that is a DATE or a COUNT, like every
 * other number on these screens, so the eye can tell a value it can compare
 * from a value it can only read.
 */
function Fact({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="fact">
      <div className="fact-label">{label}</div>
      <div className={mono ? "fact-value is-mono" : "fact-value"}>{value}</div>
    </div>
  );
}
